import styled from '@emotion/styled'
import { colors, spacing, font, radius, shadow } from '@mobi/component-library/Foundations'

type Props = {
  invalid?: boolean
  disabled?: boolean
}

function determineColors(isDisabled: boolean, isInvalid: boolean) {
  if (isDisabled) {
    return {
      background: colors.surface[100],
      border: colors.neutral[400],
      helpText: colors.neutral[400],
    }
  }

  if (isInvalid) {
    return {
      background: colors.error[50],
      border: colors.error[600],
      helpText: colors.error[600],
    }
  }

  return {
    background: colors.white,
    border: colors.neutral[300],
    helpText: colors.neutral[600],
  }
}

export const InputFieldStyled = styled.div<Props>(({ invalid = false, disabled = false }) => {
  const effectiveColors = determineColors(disabled, invalid)

  return {
    color: disabled ? colors.neutral[600] : colors.black,
    fontFamily: font.family.primary,
    ...font.size.lg,

    '& > label': {
      display: 'block',
      fontWeight: font.weight.medium,
      ...font.size.lg,
      marginBottom: spacing.smx2,
    },

    '& > div': {
      alignItems: 'center',
      backgroundColor: effectiveColors.background,
      border: `1px solid ${effectiveColors.border}`,
      borderRadius: radius.lgx1,
      overflow: 'hidden',
      boxShadow: shadow.xs,
      display: 'flex',
      height: '4.4rem',
      outlineColor: 'transparent',
      outlineStyle: 'solid',
      outlineWidth: '4px',
      transition: 'outline-color 150ms ease, border-color 150ms ease',
      WebkitTapHighlightColor: 'transparent',

      '&:focus-within': {
        borderColor: colors.lavender[300],
        outlineColor: colors.lavender[50],
      },

      '& > input': {
        width: '100%',
        background: 'none',
        border: 'none',
        boxShadow: 'none',
        caretColor: colors.studio[500],
        color: disabled ? colors.neutral[500] : colors.black,
        flex: '1 1 auto',
        font: 'inherit',
        fontSize: 'inherit',
        fontWeight: font.weight.regular,
        height: '100%',
        letterSpacing: 'inherit',
        lineHeight: 'inherit',

        '&:not(:first-child)': {
          marginInlineStart: spacing.sm,

          '@supports not (margin-inline-start: 0)': {
            marginLeft: spacing.sm,
          },
        },

        '&:not(:last-child)': {
          marginInlineEnd: spacing.lg,

          '@supports not (margin-inline-end: 0)': {
            marginRight: spacing.lg,
          },
        },

        '&::placeholder': {
          color: colors.neutral[500],
        },

        '&:disabled::placeholder': {
          color: colors.neutral[400],
        },

        '&:focus': {
          outline: 'none',
        },
      },
    },

    '& > div > :first-child': {
      paddingLeft: spacing.md,
    },

    '& > div > :last-child': {
      paddingRight: spacing.md,
    },

    '& > span': {
      display: 'flex',
      justifyContent: 'flex-start',
      gap: '0.4rem',
      color: effectiveColors.helpText,
      marginTop: spacing.smx1,
      ...font.size.md,

      '&:empty': {
        display: 'none',
      },
    },
  }
})
